import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import logger from '../../../../app/logger';
import { bindCampaignRaisedDetails } from '../../../actions';
import { fetchCampaignRaisedFresh } from '../utility/apiActions';
import { formatStreamAmount, formatStreamComments, formatStreamName, removedecimal } from '../utility/util';

const SCROLL_SPEED_PPS = 80; // Pixels per second for comfortable reading speed
const TOP_PAUSE_MS = 1200; // Pause duration at top before scrolling resumes
const BOTTOM_PAUSE_MS = 2000; // Pause duration at bottom before resetting to top

const StreamerList = props => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const formSettings = useSelector(
        state => state['features/clickandpledge']?.formSettings || {}
    );

    const listRef = useRef(null);
    const animFrameRef = useRef(null);
    const lastTimeRef = useRef(null);
    const pauseTimerRef = useRef(null);
    const isProgrammaticScrollRef = useRef(false);
    const lastProgrammaticScrollTimeRef = useRef(0);
    const isUserInteractingRef = useRef(false);
    const isHoveredRef = useRef(false);
    const resumeTimeoutRef = useRef(null);

    // Helper to find whichever element is actually overflowing/scrollable
    const getScrollContainer = useCallback(() => {
        const el = listRef.current;

        if (!el) {
            return null;
        }

        // Check if the list element itself is scrollable
        if (el.scrollHeight > el.clientHeight + 4 && el.clientHeight > 0) {
            return el;
        }

        // Check parent container (.streamer_wrapper)
        const parent = el.parentElement;

        if (parent && parent.scrollHeight > parent.clientHeight + 4 && parent.clientHeight > 0) {
            return parent;
        }

        return el;
    }, []);

    // Helper to pause auto-scroll during manual user interactions
    const pauseForManualInteraction = useCallback((durationMs = 1500) => {
        isUserInteractingRef.current = true;

        if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
        }

        resumeTimeoutRef.current = setTimeout(() => {
            isUserInteractingRef.current = false;
            lastTimeRef.current = null;
        }, durationMs);
    }, []);

    const riseDonation = amt => {
        pauseForManualInteraction(2000);
        props.raiseUpDonation(amt);
    };

    const getCampaignRaisedData = useCallback(async () => {
        const settings = formSettings;

        // Try multiple sources for CampaignID
        const campaignID = settings.CampaignDetails?.CampaignID
                || settings.campaignID
                || settings.CampaignID;

        if (campaignID) {
            try {
                const campResponse = await fetchCampaignRaisedFresh({
                    organizationId: settings.OrganizationID,
                    campaignId: campaignID
                })();

                if (campResponse) {
                    dispatch(bindCampaignRaisedDetails(campResponse));
                }
            } catch (err) {
                logger.error('getCampaignRaisedData error', err);
            }
        }
    }, [ formSettings, dispatch ]);

    useEffect(() => {
        getCampaignRaisedData();
    }, [ getCampaignRaisedData ]);

    const streamFeed = props.streamerFeed || [];

    // Main auto-scroll loop: only scrolls when content naturally overflows
    useEffect(() => {
        const scrollStep = timestamp => {
            const container = getScrollContainer();

            if (!container) {
                animFrameRef.current = requestAnimationFrame(scrollStep);

                return;
            }

            const isScrollable = container.scrollHeight > container.clientHeight + 4;

            // Only auto-scroll if not hovered, not paused, not manually interacting, and content actually overflows
            if (
                !isHoveredRef.current
                && !isUserInteractingRef.current
                && !pauseTimerRef.current
                && isScrollable
            ) {
                if (!lastTimeRef.current) {
                    lastTimeRef.current = timestamp;
                }

                const deltaTime = Math.min((timestamp - lastTimeRef.current) / 1000, 0.1);

                lastTimeRef.current = timestamp;

                const scrollIncrement = SCROLL_SPEED_PPS * deltaTime;
                const maxScroll = container.scrollHeight - container.clientHeight;

                if (container.scrollTop >= maxScroll - 2) {
                    // Reached bottom: pause briefly so user can read last item, then smoothly reset to top
                    pauseTimerRef.current = setTimeout(() => {
                        const activeContainer = getScrollContainer();

                        if (activeContainer) {
                            isProgrammaticScrollRef.current = true;
                            lastProgrammaticScrollTimeRef.current = Date.now();
                            activeContainer.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });

                            // Pause at the top after reset so user can read first item
                            pauseTimerRef.current = setTimeout(() => {
                                pauseTimerRef.current = null;
                                lastTimeRef.current = null;
                                isProgrammaticScrollRef.current = false;
                            }, TOP_PAUSE_MS);
                        } else {
                            pauseTimerRef.current = null;
                        }
                    }, BOTTOM_PAUSE_MS);
                } else {
                    isProgrammaticScrollRef.current = true;
                    lastProgrammaticScrollTimeRef.current = Date.now();
                    container.scrollTop += scrollIncrement;

                    // Reset programmatic scroll flag after a delay
                    setTimeout(() => {
                        isProgrammaticScrollRef.current = false;
                    }, 50);
                }
            } else {
                lastTimeRef.current = null;
            }

            animFrameRef.current = requestAnimationFrame(scrollStep);
        };

        animFrameRef.current = requestAnimationFrame(scrollStep);

        return () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
            }
            if (pauseTimerRef.current) {
                clearTimeout(pauseTimerRef.current);
            }
            if (resumeTimeoutRef.current) {
                clearTimeout(resumeTimeoutRef.current);
            }
        };
    }, [ getScrollContainer ]);

    // Pause on hover or explicit manual wheel, touch, or scroll interactions
    useEffect(() => {
        const el = listRef.current;
        const parent = el?.parentElement;

        const onWheelEvent = () => pauseForManualInteraction(1500);
        const onTouchStartEvent = () => pauseForManualInteraction(2000);
        const onTouchEndEvent = () => pauseForManualInteraction(1500);
        const onMouseEnterEvent = () => {
            isHoveredRef.current = true;
            lastTimeRef.current = null;
        };
        const onMouseLeaveEvent = () => {
            isHoveredRef.current = false;
            lastTimeRef.current = null;
        };
        const onScrollEvent = () => {
            // Only pause if this scroll event was not triggered by our own continuous autoscroll loop
            if (!isProgrammaticScrollRef.current && (Date.now() - lastProgrammaticScrollTimeRef.current > 150)) {
                pauseForManualInteraction(1500);
            }
        };

        const targets = [ el, parent ].filter(Boolean);

        targets.forEach(target => {
            target.addEventListener('wheel', onWheelEvent, { passive: true });
            target.addEventListener('touchstart', onTouchStartEvent, { passive: true });
            target.addEventListener('touchend', onTouchEndEvent, { passive: true });
            target.addEventListener('mouseenter', onMouseEnterEvent);
            target.addEventListener('mouseleave', onMouseLeaveEvent);
            target.addEventListener('scroll', onScrollEvent, { passive: true });
        });

        return () => {
            targets.forEach(target => {
                target.removeEventListener('wheel', onWheelEvent);
                target.removeEventListener('touchstart', onTouchStartEvent);
                target.removeEventListener('touchend', onTouchEndEvent);
                target.removeEventListener('mouseenter', onMouseEnterEvent);
                target.removeEventListener('mouseleave', onMouseLeaveEvent);
                target.removeEventListener('scroll', onScrollEvent);
            });
        };
    }, [ pauseForManualInteraction ]);

    return (
        <ul
            className = 'streamer_list'
            ref = { listRef }>
            {streamFeed.map(item => (
                <li key = { item.orderNumber || item.id }>
                    <div className = 'left_side'>
                        <p className = 'name'>
                            {formSettings.StreamFormat
                                ? formatStreamName(
                                    formSettings.StreamFormat,
                                    item,
                                    props.currency,
                                    formSettings.CampaignDetails && formSettings.CampaignDetails.EnableIntelliBooster
                                )
                                : (
                                    <>
                                        <span>{`${props.currency}${item.amount} by `}</span>
                                        {`${item.firstName || ''} ${item.lastName || ''}`.trim()}
                                    </>
                                )}
                        </p>
                        {item.comments ? (
                            <p className = 'message'>
                                {formSettings.StreamFormat
                                    ? formatStreamComments(formSettings.StreamFormat, item)
                                    : item.comments.length > 50
                                        ? `${item.comments.slice(0, 50)}...`
                                        : item.comments}
                            </p>
                        ) : null}
                    </div>
                    {formSettings.CampaignDetails && formSettings.CampaignDetails.EnableIntelliBooster ? (
                        <button
                            className = 'riseBtn'
                            onClick = { () => riseDonation(item.amount) }>
                            <span className = 'riseIcon'>
                                <svg
                                    fill = 'rgba(255,255,255,1)'
                                    height = '14'
                                    viewBox = '0 0 24 24'
                                    width = '14'
                                    xmlns = 'http://www.w3.org/2000/svg'>
                                    <path d = 'M13.0001 7.82843V20H11.0001V7.82843L5.63614 13.1924L4.22192 11.7782L12.0001 4L19.7783 11.7782L18.3641 13.1924L13.0001 7.82843Z' />
                                </svg>
                            </span>
                            {t('clickandpledge.boost')}
                        </button>
                    ) : (
                        <span className = 'amont'>
                            {formSettings.StreamFormat
                                ? formatStreamAmount(formSettings.StreamFormat, item, props.currency)
                                : `${props.currency}${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 })
                                    .format(removedecimal(item.amount))}`}
                        </span>
                    )}
                </li>
            ))}
        </ul>
    );
};

export default StreamerList;
